/// <reference types="vite/client" />
import {
  createRootRoute,
  HeadContent,
  Outlet,
  Scripts,
  useRouterState,
} from '@tanstack/react-router';
import { QueryClientProvider } from '@tanstack/react-query';
import type { ReactNode } from 'react';

import { envConfigs } from '@/config';
import { WhatsAppLeadTracker } from '@/components/analytics/whatsapp-lead-tracker';
import { getQueryClient } from '@/lib/query-client';
import libreBaskerville400 from '@fontsource/libre-baskerville/latin-400.css?inline';
import libreBaskerville700 from '@fontsource/libre-baskerville/latin-700.css?inline';
import libreBaskerville400Italic from '@fontsource/libre-baskerville/latin-400-italic.css?inline';
import globalStyles from '@/styles/globals.css?inline';

// The public site is CSS-first. Inline its small, compiled stylesheet so first
// paint is not held behind a separate render-blocking stylesheet request.
const criticalStyles = [
  libreBaskerville400,
  libreBaskerville700,
  libreBaskerville400Italic,
  globalStyles,
].join('\n');

function getHtmlLang(pathname: string) {
  return pathname === '/en' || pathname.startsWith('/en/') ? 'en-HK' : 'zh-HK';
}

export const Route = createRootRoute({
  head: () => {
    return {
      meta: [
        { charSet: 'utf-8' },
        { name: 'viewport', content: 'width=device-width, initial-scale=1' },
        { title: envConfigs.app_name },
        { name: 'description', content: envConfigs.app_description },
      ],
      links: [
        { rel: 'icon', href: '/images/doq-logo.webp', type: 'image/webp' },
        { rel: 'apple-touch-icon', href: '/images/doq-logo.webp' },
      ],
    };
  },
  component: RootComponent,
  shellComponent: RootDocument,
  notFoundComponent: NotFound,
});

function RootComponent() {
  return (
    <QueryClientProvider client={getQueryClient()}>
      <Outlet />
    </QueryClientProvider>
  );
}

function RootDocument({ children }: { children: ReactNode }) {
  const pathname = useRouterState({ select: (state) => state.location.pathname });

  return (
    <html lang={getHtmlLang(pathname)} suppressHydrationWarning>
      <head>
        <HeadContent />
        <style dangerouslySetInnerHTML={{ __html: criticalStyles }} />
        <script
          id="google-ads-loader"
          src="https://www.googletagmanager.com/gtag/js?id=AW-18389596213"
          async
        />
        <script
          id="google-ads-init"
          async
          dangerouslySetInnerHTML={{
            __html: "window.dataLayer=window.dataLayer||[];function gtag(){dataLayer.push(arguments);}gtag('js',new Date());gtag('config','AW-18389596213');",
          }}
        />
      </head>
      <body className="font-sans antialiased">
        <WhatsAppLeadTracker />
        {children}
        <Scripts />
      </body>
    </html>
  );
}

function NotFound() {
  return (
    <div className="flex min-h-screen flex-col items-center justify-center gap-4 bg-background text-foreground">
      <h1 className="text-6xl font-bold">404</h1>
      <p className="text-muted-foreground">Page not found</p>
      <a href="/" className="text-sm underline underline-offset-4">
        Back to home
      </a>
    </div>
  );
}
